import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as maplibregl from 'maplibre-gl'
import type {
  GeoJSONSource,
  Map as MapLibreMap,
  MapMouseEvent,
  MapTouchEvent,
  PointLike,
} from 'maplibre-gl'
import type { FeatureCollection } from 'geojson'

import type { Activity, Operation } from '@fit-editor/fit-core'

import { isTextEditingTarget } from '@/hooks/use-undo-redo-shortcuts'
import type { GpsPoint } from '@/lib/geo'
import {
  addToSelection,
  buildDeleteRecordsOperation,
  buildInsertAction,
  buildMoveOperation,
  buildRemoveGpsOperations,
  dragPositions,
  handlesInBox,
  interpolateLatLon,
  nearestSegment,
  planVertexHandles,
  pointCount,
  pruneSelection,
  timestampsBetween,
  toggleInSelection,
  withOverrides,
} from '@/lib/map-point-edit'
import type { HandlePlan, ScreenBox, ScreenPoint } from '@/lib/map-point-edit'
import { getThemeColors } from '@/lib/theme-colors'
import { useActivityStore } from '@/store/activity-store'
import type { ApplyOperationOptions, Theme } from '@/store/activity-store'
import { useNotificationStore } from '@/store/notification-store'

export const EDIT_VERTEX_SOURCE = 'activity-edit-vertices'
export const EDIT_VERTEX_LAYER = 'edit-vertices'

/** Pointer travel (px) before a press on a handle counts as a drag rather than a click. */
const DRAG_THRESHOLD_PX = 3
/** Half-size (px) of the box searched for a handle under the pointer -- a little forgiving for touch/mouse. */
const HIT_SLOP_PX = 7

type LatLon = { lat: number; lon: number }

type Gesture =
  | {
      kind: 'move'
      anchorIndex: number
      indices: number[]
      from: LatLon
      startPoint: ScreenPoint
      to: LatLon
      moved: boolean
    }
  | { kind: 'box'; start: ScreenPoint; last: ScreenPoint }

export type HandleStatus = 'visible' | 'zoom-in' | 'off'

export interface MapPointEditing {
  /** `zoom-in` when handles are hidden because the points are too many/too dense at this zoom. */
  handleStatus: HandleStatus
  /** Set while hovering a segment where no point can be inserted -- the reason, for display. */
  insertBlockedReason: string | null
  /** The Shift+drag selection rectangle in container pixels, while one is being drawn. */
  selectionBox: ScreenBox | null
  selectedCount: number
  /** True while an edit is being applied (further gestures are ignored until it settles). */
  busy: boolean
  removeGpsOnlyForSelection: () => Promise<void>
  deleteSelection: () => Promise<void>
  clearSelection: () => void
}

function isModalDialogOpen(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"]') != null
}

function lineData(points: readonly GpsPoint[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: points.map((p) => [p.lon, p.lat]) },
      },
    ],
  }
}

function vertexData(
  points: readonly GpsPoint[],
  indices: readonly number[],
  selected: ReadonlySet<number>,
): FeatureCollection {
  const features: FeatureCollection['features'] = []
  for (const i of indices) {
    const p = points[i]
    if (p?.timestampMs == null) continue
    features.push({
      type: 'Feature',
      id: i,
      properties: { i, selected: selected.has(p.timestampMs) ? 1 : 0 },
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
    })
  }
  return { type: 'FeatureCollection', features }
}

function createGhostElement(): HTMLDivElement {
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  el.style.width = '16px'
  el.style.height = '16px'
  el.style.borderRadius = '9999px'
  el.style.display = 'flex'
  el.style.alignItems = 'center'
  el.style.justifyContent = 'center'
  el.style.fontSize = '13px'
  el.style.fontWeight = '700'
  el.style.lineHeight = '1'
  el.style.color = '#ffffff'
  el.style.pointerEvents = 'none'
  el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.5)'
  return el
}

function styleGhost(el: HTMLDivElement, enabled: boolean, theme: Theme): void {
  const colors = getThemeColors(theme)
  el.style.background = enabled ? colors.primary : colors.mutedForeground
  el.style.opacity = enabled ? '1' : '0.85'
  el.textContent = enabled ? '+' : '×'
}

/**
 * Point-editing mode for the activity map: draggable vertex handles
 * (single point or a Shift-selected group), click-on-line insertion, and
 * remove-GPS / delete-point actions for the selection. Every edit is a
 * `fit-core` operation applied through the store
 * (`applyOperationToSelected`), so it lands in the global undo history,
 * runs in the Worker, and a rejected operation commits nothing.
 *
 * Rendering is one GeoJSON source + circle layer (never a DOM marker per
 * point); only the viewport's points get handles, and none at all when
 * they'd be too many or too dense (`planVertexHandles`). Dragging uses
 * MapLibre's map mouse/touch events -- `preventDefault()` on the press
 * suppresses drag-pan/box-zoom for that gesture -- and `e.lngLat`, which
 * MapLibre computes with pitch and 3D terrain taken into account.
 */
export function useMapPointEditing({
  map,
  styleVersion,
  enabled,
  activity,
  gpsPoints,
  theme,
  routeSourceId,
  onHover,
}: {
  map: MapLibreMap | null
  /** Bumped by the caller on every `style.load`, so this hook's layer is re-installed on a new style. */
  styleVersion: number
  enabled: boolean
  activity: Activity
  gpsPoints: GpsPoint[]
  theme: Theme
  routeSourceId: string
  onHover: (timestampMs: number | null) => void
}): MapPointEditing {
  const selection = useActivityStore((s) => s.mapEditSelectionMs)
  const setSelection = useActivityStore((s) => s.setMapEditSelectionMs)
  const setMapEditMode = useActivityStore((s) => s.setMapEditMode)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  /** Bumped on every `moveend` while editing, so the handle plan follows the camera. */
  const [cameraVersion, setCameraVersion] = useState(0)
  const [insertBlockedReason, setInsertBlockedReason] = useState<string | null>(null)
  const [selectionBox, setSelectionBox] = useState<ScreenBox | null>(null)
  const [busy, setBusy] = useState(false)

  const selectedSet = useMemo(() => new Set(selection), [selection])

  /** Positions shown instead of the real ones while a drag is in progress / being committed. */
  const overridesRef = useRef<Map<number, LatLon> | null>(null)
  /** The point a Shift+click range extends from: the last point clicked or Ctrl-toggled (like a file list's anchor). */
  const anchorRef = useRef<number | null>(null)
  /** Screen positions of the current handles; invalidated whenever the camera or the handles change. */
  const screensRef = useRef<Map<number, ScreenPoint> | null>(null)
  /** Per segment (by its earlier point's index): why no point can be inserted there, or null if it can. */
  const insertBlockedCacheRef = useRef(new Map<number, string | null>())

  // A committed edit produces a new activity: drop the drag preview and
  // any selected timestamp that no longer has a point.
  useEffect(() => {
    overridesRef.current = null
    screensRef.current = null
    insertBlockedCacheRef.current = new Map()
    const pruned = pruneSelection(useActivityStore.getState().mapEditSelectionMs, gpsPoints)
    if (pruned !== useActivityStore.getState().mapEditSelectionMs) setSelection(pruned)
  }, [gpsPoints, setSelection])

  const apply = useCallback(
    async (operation: Operation, options?: ApplyOperationOptions) => {
      setBusy(true)
      try {
        return await applyOperationToSelected(operation, options)
      } finally {
        setBusy(false)
      }
    },
    [applyOperationToSelected],
  )

  // --- Handle plan (which points get handles), recomputed after camera moves.
  const plan = useMemo<HandlePlan | null>(() => {
    if (map == null || !enabled) return null
    const bounds = map.getBounds()
    return planVertexHandles(
      gpsPoints,
      (p) => bounds.contains([p.lon, p.lat]),
      (p) => map.project([p.lon, p.lat]),
    )
    // `cameraVersion` stands in for the (external, mutable) camera state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, gpsPoints, cameraVersion])
  useEffect(() => {
    screensRef.current = null
  }, [plan])

  // Latest values for the long-lived map event handlers (bound once per map/mode).
  const latest = useRef({ activity, gpsPoints, selection, plan, theme, busy, onHover })
  useEffect(() => {
    latest.current = { activity, gpsPoints, selection, plan, theme, busy, onHover }
  })

  // --- Install / remove the handle layer.
  useEffect(() => {
    if (map == null || !enabled) return
    const colors = getThemeColors(theme)
    if (map.getSource(EDIT_VERTEX_SOURCE) == null) {
      map.addSource(EDIT_VERTEX_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (map.getLayer(EDIT_VERTEX_LAYER) == null) {
      map.addLayer({
        id: EDIT_VERTEX_LAYER,
        type: 'circle',
        source: EDIT_VERTEX_SOURCE,
        paint: {
          'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 7, 5],
          'circle-color': ['case', ['==', ['get', 'selected'], 1], colors.warning, colors.card],
          'circle-stroke-color': colors.primary,
          'circle-stroke-width': 2,
        },
      })
    }
    return () => {
      // The map may already be gone (unmount) or on a new style.
      try {
        if (map.getLayer(EDIT_VERTEX_LAYER) != null) map.removeLayer(EDIT_VERTEX_LAYER)
        if (map.getSource(EDIT_VERTEX_SOURCE) != null) map.removeSource(EDIT_VERTEX_SOURCE)
      } catch {
        // Map already removed -- nothing to clean up.
      }
    }
  }, [map, enabled, styleVersion, theme])

  /** Pushes handles (and, during a drag, the previewed route line) to the map. */
  const render = useCallback(() => {
    if (map == null) return
    const { gpsPoints: points, plan: currentPlan, selection: sel } = latest.current
    const overrides = overridesRef.current
    const shown = overrides == null ? points : withOverrides(points, overrides)
    const vertexSource = map.getSource(EDIT_VERTEX_SOURCE) as GeoJSONSource | undefined
    vertexSource?.setData(
      vertexData(shown, currentPlan?.status === 'visible' ? currentPlan.indices : [], new Set(sel)),
    )
    if (overrides != null) {
      const route = map.getSource(routeSourceId) as GeoJSONSource | undefined
      route?.setData(lineData(shown))
    }
  }, [map, routeSourceId])

  useEffect(() => {
    if (map == null || !enabled) return
    render()
  }, [map, enabled, styleVersion, plan, selectedSet, gpsPoints, render])

  // --- Selection actions (toolbar buttons and the Delete key).
  const deleteSelection = useCallback(async () => {
    const sel = latest.current.selection
    const operation = buildDeleteRecordsOperation(sel)
    if (operation == null) return
    const result = await apply(operation, {
      successTitle: `Deleted ${pointCount(sel.length)}. Undo with Ctrl+Z.`,
    })
    if (result != null) setSelection([])
  }, [apply, setSelection])

  // --- Map interaction.
  useEffect(() => {
    if (map == null || !enabled) return
    const canvas = map.getCanvas()
    const ghost = new maplibregl.Marker({ element: createGhostElement(), anchor: 'center' })
    let ghostShown = false
    let hoveredId: number | null = null
    let gesture: Gesture | null = null
    let frame: number | null = null
    let lastBlockedReason: string | null = null

    const setBlockedReason = (reason: string | null) => {
      if (reason === lastBlockedReason) return
      lastBlockedReason = reason
      setInsertBlockedReason(reason)
    }

    const screens = (): Map<number, ScreenPoint> => {
      if (screensRef.current != null) return screensRef.current
      const result = new Map<number, ScreenPoint>()
      const { plan: currentPlan, gpsPoints: points } = latest.current
      if (currentPlan?.status === 'visible') {
        for (const i of currentPlan.indices) {
          const p = points[i]
          if (p != null) result.set(i, map.project([p.lon, p.lat]))
        }
      }
      screensRef.current = result
      return result
    }

    const setHovered = (id: number | null) => {
      if (id === hoveredId) return
      if (hoveredId != null && map.getSource(EDIT_VERTEX_SOURCE) != null) {
        map.setFeatureState({ source: EDIT_VERTEX_SOURCE, id: hoveredId }, { hover: false })
      }
      hoveredId = id
      if (id != null && map.getSource(EDIT_VERTEX_SOURCE) != null) {
        map.setFeatureState({ source: EDIT_VERTEX_SOURCE, id }, { hover: true })
      }
    }

    const hideGhost = () => {
      if (ghostShown) ghost.remove()
      ghostShown = false
      setBlockedReason(null)
    }

    /** Index of the handle under `point`, nearest first, or null. */
    const vertexAt = (point: ScreenPoint): number | null => {
      if (map.getLayer(EDIT_VERTEX_LAYER) == null) return null
      const box: [PointLike, PointLike] = [
        [point.x - HIT_SLOP_PX, point.y - HIT_SLOP_PX],
        [point.x + HIT_SLOP_PX, point.y + HIT_SLOP_PX],
      ]
      const features = map.queryRenderedFeatures(box, { layers: [EDIT_VERTEX_LAYER] })
      let best: number | null = null
      let bestDistance = Number.POSITIVE_INFINITY
      const projected = screens()
      for (const f of features) {
        const i = f.properties?.i
        if (typeof i !== 'number') continue
        const s = projected.get(i)
        const d = s == null ? HIT_SLOP_PX : Math.hypot(s.x - point.x, s.y - point.y)
        if (d < bestDistance) {
          bestDistance = d
          best = i
        }
      }
      return best
    }

    /** Insert-ghost candidate at `point`: where, after which record, and whether it's allowed. */
    const insertCandidate = (point: ScreenPoint) => {
      const hit = nearestSegment(point, screens())
      if (hit == null) return null
      const { gpsPoints: points, activity: current } = latest.current
      const a = points[hit.fromIndex]
      const b = points[hit.fromIndex + 1]
      if (a?.timestampMs == null || b == null) return null
      const position = interpolateLatLon(a, b, hit.t)
      // `planInsertPoint` scans the records, so its verdict is cached per
      // segment rather than recomputed on every mouse move.
      const cache = insertBlockedCacheRef.current
      let blockedReason = cache.get(hit.fromIndex)
      if (blockedReason === undefined) {
        const action = buildInsertAction(current, a.timestampMs, position)
        blockedReason = action.ok ? null : action.reason
        cache.set(hit.fromIndex, blockedReason)
      }
      return { position, afterTimestampMs: a.timestampMs, blockedReason }
    }

    const scheduleRender = () => {
      if (frame != null) return
      frame = requestAnimationFrame(() => {
        frame = null
        render()
      })
    }

    // ---- Gesture (drag a handle / Shift+drag a selection box) ----------
    const onGestureMove = (e: MapMouseEvent | MapTouchEvent) => {
      if (gesture == null) return
      if (gesture.kind === 'box') {
        gesture.last = { x: e.point.x, y: e.point.y }
        setSelectionBox({
          x0: gesture.start.x,
          y0: gesture.start.y,
          x1: gesture.last.x,
          y1: gesture.last.y,
        })
        return
      }
      gesture.to = { lat: e.lngLat.lat, lon: e.lngLat.lng }
      if (
        !gesture.moved &&
        Math.hypot(e.point.x - gesture.startPoint.x, e.point.y - gesture.startPoint.y) >
          DRAG_THRESHOLD_PX
      ) {
        gesture.moved = true
      }
      if (!gesture.moved) return
      overridesRef.current = dragPositions(
        latest.current.gpsPoints,
        gesture.indices,
        gesture.from,
        gesture.to,
      )
      screensRef.current = null
      scheduleRender()
    }

    const detachGesture = () => {
      map.off('mousemove', onGestureMove)
      map.off('touchmove', onGestureMove)
      window.removeEventListener('mouseup', onGestureEnd)
      window.removeEventListener('touchend', onGestureEnd)
      window.removeEventListener('touchcancel', onGestureCancel)
    }

    const resetPreview = () => {
      overridesRef.current = null
      screensRef.current = null
      try {
        // Restore the real route line (the drag preview overwrote it).
        const route = map.getSource(routeSourceId) as GeoJSONSource | undefined
        route?.setData(lineData(latest.current.gpsPoints))
        render()
      } catch {
        // The map was removed while the edit was being applied.
      }
    }

    const onGestureCancel = () => {
      detachGesture()
      const current = gesture
      gesture = null
      canvas.style.cursor = ''
      if (current?.kind === 'box') setSelectionBox(null)
      else if (current?.moved) resetPreview()
    }

    function onGestureEnd() {
      detachGesture()
      const current = gesture
      gesture = null
      canvas.style.cursor = ''
      if (current == null) return
      const { gpsPoints: points, selection: sel } = latest.current

      if (current.kind === 'box') {
        setSelectionBox(null)
        const hits = handlesInBox(screens(), {
          x0: current.start.x,
          y0: current.start.y,
          x1: current.last.x,
          y1: current.last.y,
        })
        const times = hits.map((i) => points[i]?.timestampMs).filter((t): t is number => t != null)
        setSelection(addToSelection(sel, times))
        return
      }

      const anchorTime = points[current.anchorIndex]?.timestampMs ?? null
      if (!current.moved) {
        // A plain click on a handle selects just that point (and anchors Shift+click ranges).
        if (anchorTime != null) {
          anchorRef.current = anchorTime
          setSelection([anchorTime])
          latest.current.onHover(anchorTime)
        }
        return
      }
      const positions = dragPositions(points, current.indices, current.from, current.to)
      overridesRef.current = positions
      const operation = buildMoveOperation(points, positions)
      if (operation == null) {
        resetPreview()
        return
      }
      const count = positions.size
      void apply(operation, { successTitle: `Moved ${pointCount(count)}` }).then((result) => {
        // On failure nothing was committed: put the route back. On
        // success the new activity arrives as new `gpsPoints`, which
        // clears the preview (effect above).
        if (result == null) resetPreview()
      })
    }

    const beginGesture = (next: Gesture, touch: boolean) => {
      gesture = next
      hideGhost()
      if (touch) {
        map.on('touchmove', onGestureMove)
        window.addEventListener('touchend', onGestureEnd)
        window.addEventListener('touchcancel', onGestureCancel)
      } else {
        map.on('mousemove', onGestureMove)
        window.addEventListener('mouseup', onGestureEnd)
      }
    }

    const startOnHandle = (e: MapMouseEvent | MapTouchEvent, index: number, touch: boolean) => {
      const { gpsPoints: points, selection: sel } = latest.current
      const time = points[index]?.timestampMs
      if (time == null) return
      e.preventDefault()
      const selectedTimes = new Set(sel)
      const indices = selectedTimes.has(time)
        ? points.flatMap((p, i) =>
            p.timestampMs != null && selectedTimes.has(p.timestampMs) ? [i] : [],
          )
        : [index]
      const from = { lat: e.lngLat.lat, lon: e.lngLat.lng }
      canvas.style.cursor = 'grabbing'
      beginGesture(
        {
          kind: 'move',
          anchorIndex: index,
          indices,
          from,
          to: from,
          startPoint: { x: e.point.x, y: e.point.y },
          moved: false,
        },
        touch,
      )
    }

    const onMouseDown = (e: MapMouseEvent) => {
      if (e.originalEvent.button !== 0 || latest.current.busy || gesture != null) return
      const index = vertexAt(e.point)
      const shift = e.originalEvent.shiftKey
      // Ctrl (Cmd on a Mac) / Shift on a handle select like a file list:
      // Ctrl toggles the point, Shift selects every point from the anchor
      // (the last plain/Ctrl click) to this one, Ctrl+Shift adds that range.
      const additive = e.originalEvent.ctrlKey || e.originalEvent.metaKey
      if (index != null && (shift || additive)) {
        e.preventDefault()
        const { gpsPoints: points, selection: sel } = latest.current
        const time = points[index]?.timestampMs
        if (time == null) return
        if (shift) {
          anchorRef.current ??= time
          const range = timestampsBetween(points, anchorRef.current, time)
          setSelection(additive ? addToSelection(sel, range) : range)
        } else {
          anchorRef.current = time
          setSelection(toggleInSelection(sel, time))
        }
        return
      }
      if (index != null) {
        startOnHandle(e, index, false)
        return
      }
      if (shift) {
        // Shift+drag on the map: our selection box instead of MapLibre's box zoom.
        e.preventDefault()
        const start = { x: e.point.x, y: e.point.y }
        beginGesture({ kind: 'box', start, last: start }, false)
        setSelectionBox({ x0: start.x, y0: start.y, x1: start.x, y1: start.y })
      }
    }

    const onTouchStart = (e: MapTouchEvent) => {
      if (e.points.length !== 1 || latest.current.busy || gesture != null) return
      const index = vertexAt(e.point)
      if (index != null) startOnHandle(e, index, true)
    }

    // ---- Hover: handle highlight and the insert ghost ------------------
    const onMouseMove = (e: MapMouseEvent) => {
      if (gesture != null) return
      const index = vertexAt(e.point)
      setHovered(index)
      if (index != null) {
        hideGhost()
        canvas.style.cursor = 'move'
        return
      }
      const candidate = insertCandidate(e.point)
      if (candidate == null) {
        hideGhost()
        canvas.style.cursor = ''
        return
      }
      const reason = candidate.blockedReason
      const element = ghost.getElement() as HTMLDivElement
      styleGhost(element, reason == null, latest.current.theme)
      ghost.setLngLat([candidate.position.lon, candidate.position.lat])
      if (!ghostShown) ghost.addTo(map)
      ghostShown = true
      setBlockedReason(reason)
      canvas.style.cursor = reason == null ? 'copy' : 'not-allowed'
    }

    const onMouseOut = () => {
      if (gesture != null) return
      setHovered(null)
      hideGhost()
      canvas.style.cursor = ''
    }

    // ---- Click: insert on the line, or clear the selection -------------
    const onClick = (e: MapMouseEvent) => {
      if (e.originalEvent.shiftKey || latest.current.busy) return
      if (vertexAt(e.point) != null) return // handled by mousedown/mouseup
      const candidate = insertCandidate(e.point)
      if (candidate == null) {
        if (latest.current.selection.length > 0) setSelection([])
        return
      }
      const action = buildInsertAction(
        latest.current.activity,
        candidate.afterTimestampMs,
        candidate.position,
      )
      if (!action.ok) {
        useNotificationStore.getState().notify({
          variant: 'error',
          title: "Can't insert a point here",
          description: `${action.reason.charAt(0).toUpperCase()}${action.reason.slice(1)}.`,
        })
        return
      }
      hideGhost()
      void apply(action.operation, { successTitle: 'Inserted a point' }).then((result) => {
        if (result != null) setSelection([action.timestampMs])
      })
    }

    const onCameraMove = () => {
      screensRef.current = null
    }

    // ---- Keyboard: Esc clears the selection (then leaves the mode), Delete removes points.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTextEditingTarget(event.target) || isModalDialogOpen()) return
      if (event.key === 'Escape') {
        if (gesture != null) {
          onGestureCancel()
        } else if (latest.current.selection.length > 0) {
          setSelection([])
        } else {
          setMapEditMode(false)
        }
        event.preventDefault()
        return
      }
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        latest.current.selection.length > 0 &&
        !latest.current.busy &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault()
        void deleteSelection()
      }
    }

    map.on('mousedown', onMouseDown)
    map.on('touchstart', onTouchStart)
    map.on('mousemove', onMouseMove)
    map.on('mouseout', onMouseOut)
    map.on('click', onClick)
    map.on('move', onCameraMove)
    const onMoveEnd = () => setCameraVersion((v) => v + 1)
    map.on('moveend', onMoveEnd)
    window.addEventListener('keydown', onKeyDown)

    return () => {
      map.off('mousedown', onMouseDown)
      map.off('touchstart', onTouchStart)
      map.off('mousemove', onMouseMove)
      map.off('mouseout', onMouseOut)
      map.off('click', onClick)
      map.off('move', onCameraMove)
      map.off('moveend', onMoveEnd)
      window.removeEventListener('keydown', onKeyDown)
      detachGesture()
      if (frame != null) cancelAnimationFrame(frame)
      if (ghostShown) ghost.remove()
      setInsertBlockedReason(null)
      setSelectionBox(null)
      canvas.style.cursor = ''
      if (overridesRef.current != null) {
        overridesRef.current = null
        try {
          const route = map.getSource(routeSourceId) as GeoJSONSource | undefined
          route?.setData(lineData(latest.current.gpsPoints))
        } catch {
          // Map already removed.
        }
      }
    }
  }, [map, enabled, apply, deleteSelection, render, routeSourceId, setSelection, setMapEditMode])

  const removeGpsOnlyForSelection = useCallback(async () => {
    const { selection: sel, gpsPoints: points } = latest.current
    const operations = buildRemoveGpsOperations(points, sel)
    for (const [k, operation] of operations.entries()) {
      const title =
        operations.length === 1
          ? `Removed GPS from ${pointCount(sel.length)}. Undo with Ctrl+Z.`
          : `Removed GPS (${k + 1} of ${operations.length} runs). Undo with Ctrl+Z.`
      const result = await apply(operation, { successTitle: title })
      if (result == null) return // failure toast already shown; stop, keep the selection
    }
    setSelection([])
  }, [apply, setSelection])

  const clearSelection = useCallback(() => setSelection([]), [setSelection])

  let handleStatus: HandleStatus = 'off'
  if (enabled && plan != null) handleStatus = plan.status === 'visible' ? 'visible' : 'zoom-in'

  return {
    handleStatus,
    insertBlockedReason: enabled ? insertBlockedReason : null,
    selectionBox: enabled ? selectionBox : null,
    selectedCount: selection.length,
    busy,
    removeGpsOnlyForSelection,
    deleteSelection,
    clearSelection,
  }
}
